import {
  createFileRoute,
  Outlet,
  ErrorComponentProps,
} from "@tanstack/react-router";
import AuthGuardCombined from "@/components/auth/auth-guard-combined";
import DashboardLayout from "@/components/layout/dashboard-layout";
import { ErrorComponent } from "@/components/errors/error-component";
import { PermissionsProvider } from "@/components/context/permissions-provider";
import { SidebarProvider } from "@/components/context/sidebar-context";
import { ActiveAiDocumentProvider } from "@/components/context/active-ai-document";
import { CommandMenuProvider } from "@/components/context/command-menu-context";
import CommandMenu from "@/components/global-search/command-menu";
// Costura de edición: IA flotante + tracker. En builds locales (onprem/free)
// el seam los reemplaza por no-op (no referencian ai_agent).
import {
  FloatingAIAssistant,
  ModuleActivityTracker,
} from "@/lib/edition/jewel-ui";
import DevTools from "@/components/debug/dev-tools";
import { FullscreenButton } from "@/components/ui/fullscreen-button";
import { FullscreenProvider } from "@/components/context/fullscreen-context";
import VersionCheck from "@/components/layout/version-check";
import UpdateCheck from "@/components/layout/update-check";
import OfflineReferenceSync from "@/components/offline/offline-reference-sync";
import ConnectionWatchdog from "@/components/offline/connection-watchdog";
import { LocalDraftAutoSync } from "@/components/offline/local-draft-auto-sync";
import AppShellSnapshotSync from "@/components/offline/app-shell-snapshot-sync";
import { OfflineTablePrefetch } from "@/components/offline/offline-table-prefetch";
import { OfflineQuotationPdfPrefetch } from "@/components/offline/offline-quotation-pdf-prefetch";
import { OfflineInvoicePdfPrefetch } from "@/components/offline/offline-invoice-pdf-prefetch";
import OfflineUserCorrelativeSync from "@/components/offline/offline-user-correlative-sync";
import OfflineInvoiceAutoSync from "@/components/offline/offline-invoice-auto-sync";
import { LocalInvoiceDraftAutoSync } from "@/components/offline/local-invoice-draft-auto-sync";
import OfflineUserStockReservationSync from "@/components/offline/offline-user-stock-reservation-sync";
import { ErpLocalFirstProvider } from "@/components/localfirst/localfirst-provider";
import { OfflineCollectionsSync } from "@/components/localfirst/offline-collections-sync";

export const Route = createFileRoute("/_authenticated")({
  component: AuthenticatedLayout,
  errorComponent: AuthenticatedErrorComponent,
});

function AuthenticatedErrorComponent({ error, reset }: ErrorComponentProps) {
  return (
    <AuthGuardCombined>
      <PermissionsProvider>
        <CommandMenuProvider>
          <SidebarProvider>
            <FullscreenProvider>
              <DashboardLayout basePath="/">
                <ErrorComponent
                  error={error}
                  reset={reset}
                  context={{
                    route: "/_authenticated",
                  }}
                />
              </DashboardLayout>
              <FullscreenButton />
            </FullscreenProvider>
          </SidebarProvider>
        </CommandMenuProvider>
      </PermissionsProvider>
    </AuthGuardCombined>
  );
}

function AuthenticatedLayout() {
  return (
    <AuthGuardCombined>
      <PermissionsProvider>
        <ErpLocalFirstProvider>
        <CommandMenuProvider>
          <SidebarProvider>
            <FullscreenProvider>
              <ActiveAiDocumentProvider>
              <DashboardLayout basePath="/">
                <Outlet />
              </DashboardLayout>
              <CommandMenu />
              <FloatingAIAssistant />
              <ModuleActivityTracker />
              <DevTools />
              <VersionCheck />
              <UpdateCheck />
              <OfflineReferenceSync />
              <ConnectionWatchdog />
              <LocalDraftAutoSync />
              <AppShellSnapshotSync />
              <OfflineTablePrefetch />
              <OfflineQuotationPdfPrefetch />
              <OfflineInvoicePdfPrefetch />
              <OfflineUserCorrelativeSync />
              <OfflineInvoiceAutoSync />
              <LocalInvoiceDraftAutoSync />
              <OfflineUserStockReservationSync />
              <OfflineCollectionsSync />
              <FullscreenButton />
              </ActiveAiDocumentProvider>
            </FullscreenProvider>
          </SidebarProvider>
        </CommandMenuProvider>
        </ErpLocalFirstProvider>
      </PermissionsProvider>
    </AuthGuardCombined>
  );
}
